import { debounce } from 'lodash'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { DropdownOption } from 'shared/components/Dropdown/Dropdown'
import { Option } from 'shared/components/Dropdown/Dropdown.styles'
import { SearchDropdown, SearchDropdownProps } from 'shared/components/Dropdown/SearchDropdown'
import { Text } from 'shared/components/Text/Text'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import { toCamelCase } from 'shared/helpers/utils'
import { Team } from 'shared/models/team'

import { searchTeams } from '../../models/team'

export interface TeamReferenceDropdownProps
  extends Omit<SearchDropdownProps, 'options' | 'width' | 'maxHeight' | 'onChange' | 'value'> {
  width?: number
  maxHeight?: number
  value?: Team | null
  onChange?: HandlerOf<Team | null>
}

export const TeamReferenceDropdown = forwardRef(function TeamReferenceDropdown(
  props: TeamReferenceDropdownProps,
  ref: any
) {
  const { onChange, ...restProps } = props
  const [width, setWidth] = useState(424)
  const [value, setValue] = useState(props.value?.name || '')
  const [teams, setTeams] = useState<Team[]>([])
  const wrapRef = useRef<HTMLDivElement | null>(null)

  const fetchBrokers = (value?: string) =>
    searchTeams(value).then((data) => setTeams(data.results.map((team) => toCamelCase(team))))
  const [debouncedFetchBrokers] = useState(() => debounce(fetchBrokers, 300))

  useEffect(() => {
    if (wrapRef.current?.clientWidth) setWidth(wrapRef.current?.clientWidth)
  }, [])

  useEffect(() => {
    debouncedFetchBrokers(value)
  }, [debouncedFetchBrokers, value])

  useEffect(() => {
    setValue(props.value?.name || '')
  }, [props.value])

  useEffect(() => {
    if (value !== props.value?.name) {
      onChange?.(null)
    }
    // Only clear on change when you type, not when props change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  function handleTeamClick(broker: Team, closeDropdown: Handler) {
    onChange?.(broker)
    closeDropdown()
  }

  const brokersOptions: DropdownOption<string>[] = teams.map((team) => ({
    label: team.name,
    value: team.mcNumber,
    renderer: (_, closeDropdown) => (
      <Option onClick={() => handleTeamClick(team, closeDropdown)} key={team.id}>
        <div style={{ overflow: 'hidden' }}>
          <Text variant="paragraph1" fontWeight={600} styles={`white-space: nowrap;`}>
            {team.name}
          </Text>
          <Text variant="paragraph2" styles={`white-space: nowrap;`}>
            {team.id}
          </Text>
        </div>
      </Option>
    ),
  }))

  return (
    <div ref={wrapRef}>
      <SearchDropdown
        onChange={setValue}
        options={brokersOptions}
        width={width}
        maxHeight={500}
        disableOptionFilter
        disableVirtualDropdown
        ref={ref}
        {...restProps}
        value={value}
        inputProps={{
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }}
      />
    </div>
  )
})
